import {
  ClientCartItem,
  FreeShipment,
  FreeShippingProgress,
  FreeShippingStatus,
  ICartItem,
  PaidShipment,
  PaidShipmentReason,
} from "common/types/carts";
import { toDollars } from "common/utils/formatting";
import { captureException } from "common/utils/monitoring";
import { Optional } from "common/utils/types";

export type AutoshipToggleInputs = Pick<ICartItem, "is_subscribable" | "subscription">;

export const showAutoshipToggle = (cartItem: AutoshipToggleInputs) =>
  cartItem.is_subscribable && !cartItem.subscription;

export type OptionPricingInputs = Pick<
  ClientCartItem,
  "autoship_option_unit_price" | "one_time_option_unit_price"
>;

export const getCartItemOptionPricing = ({
  autoship_option_unit_price: autoship,
  one_time_option_unit_price: oneTime,
}: OptionPricingInputs) =>
  autoship == null || oneTime == null
    ? null
    : {
        subscription_discounted_price: autoship,
        non_subscription_discounted_price: oneTime,
        non_subscription_non_discounted_price: oneTime,
        discounted_price_breaks: [],
      };

export const getCartItemUnitPrice = (cartItem: ICartItem) => {
  return (
    cartItem.custom_unit_price ??
    (cartItem.subscription ? cartItem.subscription_unit_price : cartItem.default_unit_price)
  );
};

const formatShippingAmount = (cents: number) => toDollars(cents, { collapseWholeDollars: true });

const describeFlatFee = ({ cost }: PaidShipment) =>
  `Some items ship separately for ${formatShippingAmount(cost)}.`;

/** Values already reported, so a re-rendering banner reports each one once */
const reportedUnknownFreeShippingValues = new Set<string>();

type UnknownFreeShippingValue = {
  field: "status" | "reason";
  value: string;
};

/**
 * A status or reason this bundle has no copy for, e.g. one the backend added while an older
 * bundle is still loaded. Reported so the missing copy gets noticed. Callers pass the value through
 * `satisfies never`, so adding a status or reason to the type fails the build until it has copy.
 */
const reportUnknownFreeShippingValue = ({ field, value }: UnknownFreeShippingValue) => {
  const reportKey = `${field}:${value}`;
  if (reportedUnknownFreeShippingValues.has(reportKey)) {
    return;
  }
  reportedUnknownFreeShippingValues.add(reportKey);
  captureException(new Error(`Unknown free shipping ${field}: ${value}`));
};

/**
 * The free shipping banner's headline for the backend-decided status. Null for a status with no
 * copy, which hides the banner rather than showing the wrong message.
 */
export const getFreeShippingMessage = (freeShipping: FreeShippingProgress): Optional<string> => {
  switch (freeShipping.status) {
    case FreeShippingStatus.InProgress:
      return `You're ${formatShippingAmount(freeShipping.amount_remaining)} away from FREE shipping`;
    case FreeShippingStatus.Qualified:
      return "Your order ships FREE!";
    case FreeShippingStatus.QualifiedWithFees:
      return "FREE shipping on eligible items!";
    default:
      reportUnknownFreeShippingValue({
        field: "status",
        value: freeShipping.status satisfies never,
      });
      return null;
  }
};

/**
 * Names the threshold, to the cent so it agrees with the amount remaining. The subtotal note only
 * shows while in progress, where it explains the amount remaining.
 */
export const getFreeShippingHelperText = ({
  status,
  free_shipping_over,
}: FreeShippingProgress): Optional<string> => {
  if (free_shipping_over === null || free_shipping_over === undefined) {
    return null;
  }
  const thresholdText = `Orders over ${formatShippingAmount(free_shipping_over)} get free standard shipping.`;
  return status === FreeShippingStatus.InProgress
    ? `${thresholdText} Based on your subtotal before discounts.`
    : thresholdText;
};

export const getFreeShipmentLine = ({ fulfiller_name }: FreeShipment) =>
  `${fulfiller_name} items already ship free.`;

/** Says why a shipment still charges and what it costs. An unknown reason gets the generic line. */
export const getPaidShipmentDisclaimer = (shipment: PaidShipment) => {
  const amount = formatShippingAmount(shipment.cost);
  switch (shipment.reason) {
    case PaidShipmentReason.ColdChain:
      return `Cold chain items ship separately for ${amount}.`;
    case PaidShipmentReason.FastShippingOnly:
      return `Expedited delivery items ship separately for ${amount}.`;
    case PaidShipmentReason.FlatFee:
      return describeFlatFee(shipment);
    case PaidShipmentReason.UpgradedMethod:
      return `You chose ${shipment.shipping_method_name} for ${shipment.fulfiller_name} items (${amount}).`;
    default:
      reportUnknownFreeShippingValue({
        field: "reason",
        value: shipment.reason satisfies never,
      });
      return describeFlatFee(shipment);
  }
};
